import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  input,
  output,
  viewChild,
} from '@angular/core';
import { range } from 'lodash-es';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { whenTruthy } from 'ish-core/utils/operators';

import { CustomQuickorderFacade } from '../../facades/custom-quickorder.facade';

@Component({
  selector: 'custom-quick-order-quantity',
  templateUrl: './quick-order-quantity.component.html',
  styleUrls: ['./quick-order-quantity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuickOrderQuantityComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly quantityInput = viewChild<ElementRef<HTMLInputElement>>('quantityInput');
  readonly type = input<'input' | 'select' | 'counter'>('counter');
  readonly id = input<string>(uuid());
  readonly lineItemID = input<string>(undefined);
  readonly focus = input<boolean>(undefined);
  readonly quantityUpdatedLineItemID = output<string>();

  private destroy$ = new Subject<void>();
  visible$: Observable<boolean>;
  quantity$: Observable<number>;
  min$: Observable<number>;
  max$: Observable<number>;
  step$: Observable<number>;
  hasQuantityError$: Observable<boolean>;
  quantityError$: Observable<string>;
  product$: Observable<ProductView>;
  selectValues$: Observable<number[]>;
  productIndex$: Observable<number>;

  cannotIncrease$: Observable<boolean>;
  cannotDecrease$: Observable<boolean>;
  quantityChange = new Subject<EventTarget>();

  constructor(
    private context: ProductContextFacade,
    private quickOrderFacade: CustomQuickorderFacade,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.visible$ = this.context.select('displayProperties', 'quantity');
    this.quantity$ = this.context.select('quantity').pipe(filter(n => typeof n === 'number' && !isNaN(n)));
    this.min$ = this.context.select('minQuantity');
    this.max$ = this.context.select('maxQuantity');
    this.step$ = this.context.select('stepQuantity');
    this.product$ = this.context
      .select('product')
      .pipe(
        tap(() =>
          this.context.get('productIndex') > -1
            ? this.context.get('product')
            : this.context.set('productIndex', () => -1)
        )
      );
    this.hasQuantityError$ = this.context.select('hasQuantityError');
    this.quantityError$ = this.context.select('quantityError');

    this.productIndex$ = this.context.select('productIndex');
    this.selectValues$ = combineLatest([this.min$, this.max$, this.step$]).pipe(
      map(([min, max, step]) => range(min, max + 1, step))
    );

    this.context.connect(
      'quantity',
      this.context.select('product').pipe(
        whenTruthy(),
        map(p => p.stepQuantity)
      ),
      (contextState, stepQuantity) => {
        const override = contextState.quantity === undefined || (contextState.quantity === 1 && stepQuantity > 1);

        if (override) {
          contextState.quantity = stepQuantity;
        }
        return contextState.quantity;
      }
    );

    this.cannotIncrease$ = combineLatest([
      this.max$,
      this.quantity$,
      this.product$,
      this.context.select('loading'),
    ]).pipe(map(([max, quantity, product, loading]) => quantity >= max || product.failed || loading));
    this.cannotDecrease$ = combineLatest([
      this.min$,
      this.quantity$,
      this.product$,
      this.context.select('loading'),
    ]).pipe(map(([min, quantity, product, loading]) => quantity <= min || product.failed || loading));

    this.quantityChange.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => {
      this.changeOnEnter();
    });
  }

  ngAfterViewInit() {
    this.visible$.pipe(whenTruthy(), take(1), takeUntil(this.destroy$)).subscribe(visible => {
      if (this.focus() && visible) {
        this.cdRef.detectChanges();
        const input = this.quantityInput();
        if (input) {
          const quantity = input.nativeElement;
          quantity.focus();
          this.cdRef.markForCheck();
        }
      }
    });
  }

  private setValue(value: number) {
    this.context.set('quantity', () => value);
  }

  productLoading() {
    return this.context.get('loading');
  }
  private setNextValue(value: number) {
    const max = this.context.get('maxQuantity');
    const min = this.context.get('minQuantity');
    const step = this.context.get('stepQuantity');
    this.setValue(value > max ? max : value < min ? min : value - (value % step));
  }

  increase() {
    if (!isNaN(this.context.get('quantity'))) {
      this.setNextValue(this.context.get('quantity') + this.context.get('stepQuantity'));
      const lineItemID = this.lineItemID();
      if (lineItemID) {
        this.quickOrderFacade.updateQuickOrderItem(lineItemID, {
          productSKU: this.context.get('sku'),
          quantity: this.context.get('quantity'),
        });
        this.emitQuantityUpdatedEvent('');
      }
    }
  }

  decrease() {
    if (!isNaN(this.context.get('quantity'))) {
      this.setNextValue(this.context.get('quantity') - this.context.get('stepQuantity'));
      const lineItemID = this.lineItemID();
      if (lineItemID) {
        this.quickOrderFacade.updateQuickOrderItem(lineItemID, {
          productSKU: this.context.get('sku'),
          quantity: this.context.get('quantity'),
        });
        this.emitQuantityUpdatedEvent('');
      }
    }
  }

  changeOnEnter() {
    const lineItemID = this.lineItemID();
    if (
      lineItemID &&
      this.context.get('quantity') > 0 &&
      !isNaN(this.context.get('quantity')) &&
      !this.context.get('hasQuantityError')
    ) {
      this.quickOrderFacade.updateQuickOrderItem(lineItemID, {
        productSKU: this.context.get('sku'),
        quantity: this.context.get('quantity'),
      });
      this.emitQuantityUpdatedEvent('enter');
    }
  }

  change(target: EventTarget) {
    this.setValue(Number.parseInt((target as HTMLDataElement).value, 10));
  }

  emitQuantityUpdatedEvent(type: string) {
    if (type === 'enter') {
      this.quantityUpdatedLineItemID.emit(this.lineItemID());
    } else {
      this.quantityUpdatedLineItemID.emit('changeThroughIncrementOrDecrement');
    }
  }

  isButtonDisable$() {
    return combineLatest([this.product$, this.productIndex$]).pipe(
      map(([product, index]) => !product.available || (product.sku && index > -1 ? true : false)),
      takeUntil(this.destroy$)
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
